import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ConfidencePicker from '@/components/premium/planner/ConfidencePicker';

const PASS_KEYS = [
  ['learn', 'W', 'Watched'],
  ['practice', 'P', 'Practised'],
  ['revision', 'R', 'Revised'],
];

/** Every chapter in the portion, how far it has got, and how confident the
 * student feels. Weak chapters quietly earn more time in the plan. */
export function SubjectsTab({ exam, plan, onConfidence }) {
  const [open, setOpen] = useState({});
  const subjects = exam?.subjects || [];

  const progress = {};
  for (const day of plan?.days || []) {
    for (const item of day.items) {
      const chapter = item.chapter || '';
      if (!chapter) continue;
      const key = `${item.subject}::${chapter}`;
      progress[key] = progress[key] || { total: 0, done: 0, passes: {} };
      progress[key].total += 1;
      if (item.done) progress[key].done += 1;
      progress[key].passes[item.node_type] = progress[key].passes[item.node_type] || item.done;
    }
  }

  return (
    <div data-testid="planner-subjects">
      <h3 className="font-heading text-xl font-bold text-slate-900">Subjects</h3>
      <p className="font-body text-xs md:text-sm text-slate-500 mt-0.5 max-w-2xl">
        Every chapter in this exam's portion, how far you have got with it, and how confident you feel.
        Mark a chapter <span className="font-heading text-rose-600">Weak</span> and the planner gives it
        half an hour more; <span className="font-heading text-emerald-600">Strong</span> trims it a little.
      </p>
      <div className="flex flex-wrap gap-2 mt-2">
        {PASS_KEYS.map(([key, short, label]) => (
          <span key={key} className="inline-flex items-center gap-1 font-body text-[11px] text-slate-500">
            <span className="w-4 h-4 rounded bg-slate-200 text-slate-600 font-heading text-[9px] flex items-center justify-center">{short}</span>
            {label}
          </span>
        ))}
      </div>

      <div className="space-y-2 mt-4">
        {!subjects.length && (
          <p className="font-body text-sm text-slate-500" data-testid="planner-subjects-empty">
            No subjects in this exam yet — add them under Map portion on the Exams page.
          </p>
        )}
        {subjects.map(subject => {
          const chapters = subject.effective_portion || [];
          const isOpen = open[subject.subject] ?? true;
          const mapped = subject.mapping_completeness || 0;
          return (
            <div key={subject.subject} className="rounded-2xl border border-slate-200 bg-white"
              data-testid={`planner-subject-${subject.subject.replace(/\s+/g, '-').toLowerCase()}`}>
              <button onClick={() => setOpen(o => ({ ...o, [subject.subject]: !isOpen }))}
                data-testid={`planner-subject-toggle-${subject.subject.replace(/\s+/g, '-').toLowerCase()}`}
                className="w-full flex items-center gap-2 p-3 text-left">
                {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <span className="w-1.5 h-6 rounded-full bg-indigo-400 flex-shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block font-heading text-base text-slate-900 truncate">{subject.subject}</span>
                  <span className="block font-body text-[11px] text-slate-400">
                    {chapters.length} chapter{chapters.length === 1 ? '' : 's'}
                    {subject.exam_date_ist ? ` · paper on ${subject.exam_date_ist}` : ''}
                  </span>
                </span>
                <span className="font-heading text-[11px] text-slate-500 flex-shrink-0">{mapped}% mapped</span>
              </button>

              {isOpen && (
                <ul className="divide-y divide-slate-100 border-t border-slate-100">
                  {chapters.map(chapter => {
                    const stat = progress[`${subject.subject}::${chapter}`] || { total: 0, done: 0, passes: {} };
                    const level = (subject.confidence || {})[chapter] || 'ok';
                    return (
                      <li key={chapter} className="flex items-center gap-2 px-3 py-2 flex-wrap"
                        data-testid={`planner-chapter-${chapter.replace(/\s+/g, '-').toLowerCase()}`}>
                        <span className="font-body text-[13px] md:text-sm text-slate-800 flex-1 min-w-[140px]">{chapter}</span>
                        <span className="flex gap-1 flex-shrink-0">
                          {PASS_KEYS.map(([key, short]) => (
                            <span key={key} title={key}
                              className={`w-5 h-5 rounded font-heading text-[9px] flex items-center justify-center ${
                                stat.passes[key] ? 'bg-emerald-500 text-white' : key in stat.passes ? 'bg-slate-200 text-slate-500' : 'bg-slate-100 text-slate-300'}`}>
                              {short}
                            </span>
                          ))}
                        </span>
                        <ConfidencePicker value={level}
                          testId={`confidence-${subject.subject.replace(/\s+/g, '-').toLowerCase()}-${chapter.replace(/\s+/g, '-').toLowerCase()}`}
                          onChange={(next) => onConfidence(subject.subject, chapter, next)} />
                      </li>
                    );
                  })}
                  {!chapters.length && (
                    <li className="px-3 py-2 font-body text-[12px] text-slate-400">
                      No chapters ticked for this subject yet.
                    </li>
                  )}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SubjectsTab;
